export type PortalTheme = 'client' | 'provider' | 'backoffice'

const PORTAL_THEME_KEY = 'portal-theme'
const THEME_MODE_KEY = 'theme'

const portalClasses: PortalTheme[] = ['client', 'provider', 'backoffice']

const emitThemeChange = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('wasilah-theme-change'))
}

export const setPortalTheme = (portal: PortalTheme) => {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  for (const cls of portalClasses) {
    root.classList.remove(`theme-${cls}`)
  }

  root.classList.add(`theme-${portal}`)
  root.setAttribute('data-portal', portal)

  try {
    localStorage.setItem(PORTAL_THEME_KEY, portal)
  } catch {
    // Ignore storage failures (private mode, disabled storage)
  }

  emitThemeChange()
}

export const setDarkMode = (isDark: boolean) => {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  root.style.colorScheme = isDark ? 'dark' : 'light'

  try {
    localStorage.setItem(THEME_MODE_KEY, isDark ? 'dark' : 'light')
  } catch {
    // Ignore storage failures
  }

  emitThemeChange()
}

const getInitialPortal = (): PortalTheme => {
  if (typeof document === 'undefined') return 'client'

  const root = document.documentElement

  for (const portal of portalClasses) {
    if (root.classList.contains(`theme-${portal}`)) {
      return portal
    }
  }

  const dataPortal = root.getAttribute('data-portal')
  if (dataPortal === 'client' || dataPortal === 'provider' || dataPortal === 'backoffice') {
    return dataPortal
  }

  return 'client'
}

export const initThemeFromLocalStorage = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  let portal = getInitialPortal()
  let darkMode = document.documentElement.classList.contains('dark')
  const hasExplicitPortalClass = portalClasses.some((portalClass) =>
    document.documentElement.classList.contains(`theme-${portalClass}`)
  )

  try {
    const storedPortal = localStorage.getItem(PORTAL_THEME_KEY)
    if (
      !hasExplicitPortalClass &&
      (storedPortal === 'client' || storedPortal === 'provider' || storedPortal === 'backoffice')
    ) {
      portal = storedPortal
    }

    const storedTheme = localStorage.getItem(THEME_MODE_KEY)
    if (storedTheme === 'dark' || storedTheme === 'light') {
      darkMode = storedTheme === 'dark'
    } else {
      darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
    }
  } catch {
    darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  setPortalTheme(portal)
  setDarkMode(darkMode)
}
